import * as THREE from 'three';
import { createSiteObject, type SiteObject } from './SiteObject';
import { propCatalog, type PropCatalog, type PropDefinition } from './PropCatalog';
import { GltfSiteObjectProvider, loadSiteModel } from './GltfSiteObjectModel';
import { siteModels, type SiteObjectModelRegistry } from './SiteObjectModelSource';

/**
 * Turns catalog records into placeable site objects, and asset files into registered providers.
 *
 * This is the only place that knows both halves — that `bench-painted-wood` is a record with a size
 * and a licence *and* a file on disk. `SiteObjectWorld` knows only the registry; `PropCatalog` knows
 * only the records; neither imports a loader. Keeping the join here is what lets the application ship
 * without assets, or with a different asset set, without either of those files changing.
 *
 * ## Loading is lazy and optimistic
 *
 * {@link createProp} returns a usable record immediately and kicks off the download in the
 * background. The object appears at once as placeholder massing — correctly sized, because the size
 * came from the catalog rather than from the mesh — and swaps to the authored geometry when the file
 * lands, via the registry's change notification.
 *
 * That ordering is the point rather than an optimisation. A designer dragging a bench onto a slope
 * should not wait on a network round-trip to find out where it went, and a project opened offline
 * should still be a project.
 */

/** Resolves a catalog `file` name to a URL the loader can fetch. */
export type PropAssetResolver = (file: string) => string | undefined;

/**
 * Default resolver: the bundler's view of `src/assets/props`.
 *
 * `import.meta.glob` is eager over URLs only — it emits the asset and hands back a hashed path, so
 * the .glb files are content-addressed and cacheable without any of them being parsed at start-up.
 */
const bundledAssets = import.meta.glob('../assets/props/*.glb', {
  query: '?url',
  import: 'default',
  eager: true,
});

export function bundledPropUrl(file: string): string | undefined {
  // Checked at runtime rather than asserted: `import.meta.glob` is typed as `unknown` values without
  // vite/client in scope, and a catalog naming a file the bundler never saw should return undefined
  // — which the caller already handles as "keep the massing" — not crash on a bad cast.
  const url = bundledAssets[`../assets/props/${file}`];
  return typeof url === 'string' ? url : undefined;
}

export interface PropLibraryOptions {
  catalog?: PropCatalog;
  registry?: SiteObjectModelRegistry;
  resolveUrl?: PropAssetResolver;
  /** Injected for tests; defaults to the real glTF loader. */
  load?: (url: string) => Promise<THREE.Object3D>;
}

export class PropLibrary {
  private readonly catalog: PropCatalog;
  private readonly registry: SiteObjectModelRegistry;
  private readonly resolveUrl: PropAssetResolver;
  private readonly load: (url: string) => Promise<THREE.Object3D>;

  /**
   * In-flight and settled loads, keyed by prop id.
   *
   * Keyed by *promise* rather than by a boolean so that twelve chairs placed in one gesture trigger
   * one download between them, and so a caller that wants to await readiness can.
   */
  private readonly loading = new Map<string, Promise<boolean>>();

  constructor(options: PropLibraryOptions = {}) {
    this.catalog = options.catalog ?? propCatalog;
    this.registry = options.registry ?? siteModels;
    this.resolveUrl = options.resolveUrl ?? bundledPropUrl;
    this.load = options.load ?? ((url) => loadSiteModel(url));
  }

  get definitions(): PropDefinition[] {
    return this.catalog.list();
  }

  get(id: string): PropDefinition | undefined {
    return this.catalog.get(id);
  }

  /**
   * The catalog record behind a placed object, or undefined when it names no asset.
   *
   * Keyed off `modelId` rather than off `kind`, because any kind may name a model — a shed with a
   * good mesh is still a building. The capture manifest calls this to describe a prop by what it
   * actually is instead of by the box that stands in for it while the file downloads.
   */
  definitionFor(object: { modelId?: string }): PropDefinition | undefined {
    return object.modelId === undefined ? undefined : this.catalog.get(object.modelId);
  }

  /**
   * Create a placeable record for a catalog prop.
   *
   * Dimensions, colours, and clearance come from the catalog, so the object is right-sized before any
   * geometry exists — which is what makes the placeholder honest rather than a guess that jumps when
   * the asset arrives.
   */
  createProp(
    propId: string,
    options: { position?: { x: number; y: number; z: number }; rotationY?: number } = {},
  ): SiteObject {
    const definition = this.catalog.get(propId);
    if (!definition) throw new Error(`Unknown prop: ${propId}`);

    // Start the download, but do not wait for it. Placement is a synchronous gesture.
    void this.ensureLoaded(propId);

    return createSiteObject({
      kind: 'prop',
      name: definition.name,
      widthMeters: definition.size.widthMeters,
      depthMeters: definition.size.depthMeters,
      modelId: propId,
      // Spread rather than pass-through: under `exactOptionalPropertyTypes` an explicit `undefined`
      // is not the same as an absent key, and `createSiteObject`'s own defaults are the ones we want.
      ...(options.position ? { position: options.position } : {}),
      ...(options.rotationY !== undefined ? { rotationY: options.rotationY } : {}),
      overrides: {
        heightMeters: definition.size.heightMeters,
        wallColor: definition.colors.body,
        roofColor: definition.colors.trim,
        clearanceMeters: definition.clearanceMeters,
      },
    });
  }

  /**
   * Fetch and register a prop's asset. Resolves `true` once the provider is registered, `false` when
   * the asset could not be loaded.
   *
   * A failure is reported and swallowed rather than thrown. The consequence of a missing asset is
   * already defined and already benign — the object keeps its placeholder massing — so propagating
   * the error would turn a degraded prop into a broken placement gesture.
   */
  ensureLoaded(propId: string): Promise<boolean> {
    const existing = this.loading.get(propId);
    if (existing) return existing;

    const definition = this.catalog.get(propId);
    if (!definition) return Promise.resolve(false);
    if (this.registry.has(propId)) return Promise.resolve(true);

    const url = this.resolveUrl(definition.file);
    if (!url) {
      console.warn(`Prop asset not found for "${propId}" (${definition.file}) — using massing.`);
      return Promise.resolve(false);
    }

    const promise = this.load(url)
      .then((prototype) => {
        this.registry.register(
          propId,
          new GltfSiteObjectProvider(propId, prototype, {
            fit: definition.fit,
            style: definition.style,
            yawOffset: THREE.MathUtils.degToRad(definition.yawOffsetDegrees),
            sinkMeters: definition.sinkMeters,
          }),
        );
        return true;
      })
      .catch((error: unknown) => {
        console.warn(`Failed to load prop "${propId}":`, error);
        // Dropped from the map so a later attempt can retry — a transient network failure should not
        // permanently strip the prop from a session.
        this.loading.delete(propId);
        return false;
      });

    this.loading.set(propId, promise);
    return promise;
  }

  /**
   * Load every asset a set of objects references. Called after opening a project so its props upgrade
   * from massing in one pass rather than one at a time.
   */
  async preload(objects: readonly SiteObject[]): Promise<void> {
    const ids = new Set<string>();
    for (const object of objects) {
      if (object.modelId && this.catalog.has(object.modelId)) ids.add(object.modelId);
    }
    await Promise.all([...ids].map((id) => this.ensureLoaded(id)));
  }

  /** Load the whole library. For a picker that wants thumbnails, or a build that prefers eager cost. */
  async preloadAll(): Promise<void> {
    await Promise.all(this.definitions.map((definition) => this.ensureLoaded(definition.id)));
  }
}

/** The library the application uses. */
export const propLibrary = new PropLibrary();
